/**
 * validations.js
 * Schemas Zod centralizados para todos los formularios del frontend.
 * Usados con React Hook Form + @hookform/resolvers/zod.
 *
 * Cada schema refleja exactamente las mismas reglas que el backend
 * (defensa en profundidad: si el frontend falla, el backend igual rechaza).
 */
import { z } from 'zod';

// ─── Primitivos reutilizables ─────────────────────────────────────────────────

/**
 * Nombre / Apellido
 * - Solo letras (incluyendo acentuadas: á é í ó ú ü ñ etc.)
 * - Un solo espacio entre palabras (nombres compuestos permitidos)
 * - Sin números, símbolos ni emojis
 * - 2-50 caracteres, sin espacios al inicio/final
 */
const nombreSchema = z
  .string()
  .min(1, 'Este campo es requerido')
  .transform(v => v.trim())
  .pipe(
    z
      .string()
      .min(2, 'Debe tener al menos 2 caracteres')
      .max(50, 'No puede exceder 50 caracteres')
      .regex(
        /^[a-zA-ZÀ-ÿ]+( [a-zA-ZÀ-ÿ]+)*$/,
        'Solo letras. Un espacio entre palabras. Sin números ni símbolos'
      )
  );

/**
 * Email
 * - Formato RFC válido, sin espacios
 * - Se normaliza a minúsculas antes de enviar
 */
const emailSchema = z
  .string()
  .min(1, 'El correo es requerido')
  .regex(
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
    'Formato de correo inválido (ejemplo: usuario@correo.com)'
  )
  .transform(v => v.trim().toLowerCase());

/**
 * Contraseña segura
 * - 8-64 caracteres, sin espacios
 * - Requiere: mayúscula, minúscula, número, carácter especial
 */
const passwordSchema = z
  .string()
  .min(8, 'La contraseña debe tener al menos 8 caracteres')
  .max(64, 'La contraseña no puede exceder 64 caracteres')
  .regex(/^\S+$/, 'La contraseña no puede contener espacios')
  .regex(/[A-Z]/, 'Debe incluir al menos una letra mayúscula')
  .regex(/[a-z]/, 'Debe incluir al menos una letra minúscula')
  .regex(/[0-9]/, 'Debe incluir al menos un número')
  .regex(
    /[!@#$%^&*()\-_=+[\]{};:'",.<>/?\\|`~]/,
    'Debe incluir al menos un carácter especial (!@#$%^&*...)'
  );

/**
 * Teléfono (solo la parte numérica, sin el prefijo de país)
 * - Solo dígitos 0-9, 7 a 15 caracteres
 */
const telefonoSchema = z
  .string()
  .min(1, 'El número de teléfono es requerido')
  .regex(/^[0-9]{7,15}$/, 'Solo dígitos, entre 7 y 15 números');

/**
 * Número de documento / identificación
 * - Solo dígitos, mínimo 6
 */
const documentoSchema = z
  .string()
  .min(1, 'El número de documento es requerido')
  .min(6, 'El documento debe tener al menos 6 dígitos')
  .regex(/^[0-9]+$/, 'El documento solo puede contener números');

// ─── Schema: Login ────────────────────────────────────────────────────────────

export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'El correo es requerido')
    .regex(
      /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
      'Formato de correo inválido'
    ),
  password: z.string().min(1, 'La contraseña es requerida'),
});

// ─── Schema: Registro ─────────────────────────────────────────────────────────

export const registerSchema = z
  .object({
    name: nombreSchema,
    last_name: nombreSchema,
    email: emailSchema,
    confirmEmail: z.string().min(1, 'Por favor confirma tu correo electrónico'),
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Por favor confirma tu contraseña'),
    birthdate: z
      .string()
      .min(1, 'La fecha de nacimiento es requerida')
      .refine(val => !isNaN(new Date(val).getTime()), 'Fecha inválida')
      .refine(
        val => new Date(val) <= new Date(),
        'La fecha de nacimiento no puede ser una fecha futura'
      )
      .refine(val => {
        const ageMs = Date.now() - new Date(val).getTime();
        const ageyears = ageMs / (365.25 * 24 * 3600 * 1000);
        return ageyears >= 18;
      }, 'Debes tener al menos 18 años para registrarte'),
    documentType: z.enum(['cedula', 'pasaporte'], {
      errorMap: () => ({ message: 'Selecciona un tipo de documento válido' }),
    }),
    documentNumber: documentoSchema,
    // El código de país viene del <select>
    phoneCode: z.enum(['+58', '+57', '+1'], {
      errorMap: () => ({ message: 'Selecciona un código de país válido' }),
    }),
    phoneNumber: telefonoSchema,
  })
  // Validaciones cruzadas (refinements) después de definir todos los campos
  .refine(data => data.email === data.confirmEmail, {
    message: 'Los correos electrónicos no coinciden',
    path: ['confirmEmail'],
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

// ─── Schema: Recuperar contraseña ────────────────────────────────────────────

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

// ─── Schema: Restablecer contraseña ──────────────────────────────────────────

export const resetPasswordSchema = z
  .object({
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, 'Por favor confirma tu contraseña'),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

// ─── Schema: Completar perfil (Google Login) ──────────────────────────────────

export const completarRegistroSchema = z.object({
  tipoDoc: z.enum(['Cédula', 'Pasaporte', 'RUT', 'Otro'], {
    errorMap: () => ({ message: 'Selecciona un tipo de documento válido' }),
  }),
  documento: documentoSchema,
  direccion: z
    .string()
    .transform(v => v.trim())
    .pipe(
      z
        .string()
        .min(5, 'La dirección debe tener al menos 5 caracteres')
        .max(200, 'La dirección no puede exceder 200 caracteres')
    ),
  telefonoPais: z.enum(['+57', '+58', '+1', '+54'], {
    errorMap: () => ({ message: 'Selecciona un código de país válido' }),
  }),
  telefono: telefonoSchema,
});

// ─── Schema: Bus ─────────────────────────────────────────────────────────────

export const busSchema = z.object({
  plate: z
    .string()
    .trim()
    .min(5, 'La placa debe tener al menos 5 caracteres')
    .max(10, 'La placa no puede exceder 10 caracteres')
    .regex(
      /^[A-Za-z0-9-]+$/,
      'Solo letras, números y guiones (-). Ejemplo: ABC-123'
    ),
  bus_type_id: z.string().min(1, 'Selecciona un tipo de bus'),
  driver_id:   z.string().optional().default(''),
  status:      z.enum(['available', 'maintenance', 'outservice']).optional(),
});

// ─── Schema: Terminal ─────────────────────────────────────────────────────────

export const terminalSchema = z.object({
  name: z
    .string()
    .trim()
    .min(3, 'El nombre debe tener al menos 3 caracteres')
    .max(100, 'Máximo 100 caracteres'),
  address: z
    .string()
    .trim()
    .min(5, 'La dirección debe tener al menos 5 caracteres')
    .max(200, 'Máximo 200 caracteres'),
  tel_number: z
    .string()
    .transform(v => v.replace(/[\s\-\+\(\)]/g, '')) // Normalizar al guardar
    .pipe(
      z.string().regex(/^[0-9]{7,15}$/, 'Solo dígitos, entre 7 y 15 números')
    ),
});

// ─── Schema: Empleado / Usuario admin ────────────────────────────────────────
// Factory que recibe isEditing para ajustar reglas de contraseña

export const makePersonSchema = (isEditing = false) =>
  z
    .object({
      name: z
        .string()
        .trim()
        .min(2, 'Mínimo 2 caracteres')
        .max(50, 'Máximo 50 caracteres')
        .regex(/^[a-zA-ZÀ-ÿ]+( [a-zA-ZÀ-ÿ]+)*$/, 'Solo letras. Sin números ni símbolos'),
      last_name: z
        .string()
        .trim()
        .min(2, 'Mínimo 2 caracteres')
        .max(50, 'Máximo 50 caracteres')
        .regex(/^[a-zA-ZÀ-ÿ]+( [a-zA-ZÀ-ÿ]+)*$/, 'Solo letras. Sin números ni símbolos'),
      identification: z
        .string()
        .trim()
        .min(6, 'El documento debe tener al menos 6 dígitos')
        .max(15, 'Máximo 15 dígitos')
        .regex(/^[0-9]+$/, 'Solo se permiten números'),
      birthdate: z
        .string()
        .min(1, 'La fecha de nacimiento es requerida')
        .refine(val => !isNaN(new Date(val).getTime()), 'Fecha inválida')
        .refine(val => new Date(val) <= new Date(), 'No puede ser una fecha futura'),
      mail: z
        .string()
        .min(1, 'El correo es requerido')
        .regex(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, 'Formato de correo inválido'),
      tel_number: z
        .string()
        .min(1, 'El teléfono es requerido')
        .transform(v => v.replace(/[\s\-\+\(\)]/g, ''))
        .pipe(z.string().regex(/^[0-9]{7,15}$/, 'Solo dígitos, entre 7 y 15 números')),
      // Rol del usuario
      role: z.enum(['client', 'employee', 'admin'], {
        errorMap: () => ({ message: 'Selecciona un rol válido' })
      }).optional().default('client'),
      // Cargo (solo empleados; para usuarios queda vacío)
      position: z.string().optional().default(''),
      // Contraseña y oldPassword son opcionales en Zod — reglas de negocio en superRefine
      password:    z.string().optional().default(''),
      oldPassword: z.string().optional().default(''),
    })
    .superRefine((data, ctx) => {
      // Crear: contraseña obligatoria y segura
      if (!isEditing && !data.password) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: 'La contraseña es requerida para nuevos registros' });
      }
      if (data.password) {
        if (data.password.length < 8)
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: 'La contraseña debe tener al menos 8 caracteres' });
        if (!/[A-Z]/.test(data.password))
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: 'Debe incluir al menos una letra mayúscula' });
        if (!/[a-z]/.test(data.password))
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: 'Debe incluir al menos una letra minúscula' });
        if (!/[0-9]/.test(data.password))
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: 'Debe incluir al menos un número' });
        if (!/[^A-Za-z0-9]/.test(data.password))
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: 'Debe incluir al menos un carácter especial' });
        // Editar: se requiere contraseña antigua para cambiar
        if (isEditing && !data.oldPassword)
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['oldPassword'], message: 'Indica la contraseña actual para poder cambiarla' });
      }
    });

// Schema específico para empleado (agrega validación de edad mínima 18 y cargo obligatorio)
export const makeEmployeeSchema = (isEditing = false) =>
  makePersonSchema(isEditing).superRefine((data, ctx) => {
    if (data.birthdate) {
      const age = (Date.now() - new Date(data.birthdate).getTime()) / (365.25 * 24 * 3600 * 1000);
      if (age < 18)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['birthdate'], message: 'El empleado debe ser mayor de 18 años' });
    }
    if (!data.position)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['position'], message: 'Selecciona un cargo' });
  });

// ─── Schema: Ruta ─────────────────────────────────────────────────────────────

export const routeSchema = z
  .object({
    date:                  z.string().min(1, 'La fecha es requerida'),
    departing_time:        z.string().min(1, 'La hora de salida es requerida'),
    arrival_time:          z.string().min(1, 'La hora de llegada es requerida'),
    departure_terminal_id: z.string().min(1, 'Selecciona el terminal de origen'),
    arrival_terminal_id:   z.string().min(1, 'Selecciona el terminal de destino'),
    distance: z
      .string()
      .min(1, 'La distancia es requerida')
      .refine(v => Number(v) > 0, 'La distancia debe ser mayor a 0 km'),
    bus_id: z.string().min(1, 'Selecciona un bus'),
    price: z
      .string()
      .min(1, 'El precio es requerido')
      .refine(v => Number(v) > 0, 'El precio debe ser mayor a 0'),
  })
  .refine(
    data => data.departure_terminal_id !== data.arrival_terminal_id,
    { message: 'El origen y el destino deben ser diferentes', path: ['arrival_terminal_id'] }
  );

// ─── Schema: Viaje (Course) ───────────────────────────────────────────────────

export const courseSchema = z.object({
  route_id: z.string().min(1, 'Selecciona una ruta para activar'),
  status:   z.enum(['programmed', 'oncurse', 'ended', 'cancel']).optional(),
});

// ─── Schema: Promoción ────────────────────────────────────────────────────────

export const promotionSchema = z
  .object({
    Name: z
      .string()
      .trim()
      .min(3, 'El nombre debe tener al menos 3 caracteres')
      .max(100, 'Máximo 100 caracteres'),
    Description: z.string().trim().max(500, 'Máximo 500 caracteres').optional().default(''),
    Code: z
      .string()
      .trim()
      .max(50, 'El código no puede exceder 50 caracteres')
      .regex(/^[A-Za-z0-9_-]*$/, 'Solo letras, números, guiones y guiones bajos')
      .optional()
      .default(''),
    Discount: z
      .string()
      .min(1, 'El valor del descuento es requerido')
      .refine(v => Number(v) >= 1, 'El descuento debe ser al menos 1'),
    Discount_type: z.enum(['percentage', 'fixed'], {
      errorMap: () => ({ message: 'Tipo de descuento inválido' }),
    }),
    Start_date: z.string().min(1, 'La fecha de inicio es requerida'),
    End_date:   z.string().min(1, 'La fecha de fin es requerida'),
    Apply_to: z.enum(['all', 'specific'], {
      errorMap: () => ({ message: 'Selecciona a qué aplica la promoción' }),
    }),
  })
  .refine(
    data => new Date(data.End_date) >= new Date(data.Start_date),
    { message: 'La fecha de fin debe ser igual o posterior a la fecha de inicio', path: ['End_date'] }
  )
  .refine(
    data => !(data.Discount_type === 'percentage' && Number(data.Discount) > 100),
    { message: 'El porcentaje no puede exceder 100%', path: ['Discount'] }
  );

// ─── Utilidad: Fortaleza de contraseña ───────────────────────────────────────

/**
 * Calcula la fortaleza de una contraseña en tiempo real.
 * Retorna null si el campo está vacío.
 *
 * @param {string} password
 * @returns {{ level: 'weak'|'medium'|'strong', label: string, percent: number } | null}
 */
export function getPasswordStrength(password) {
  if (!password) return null;

  let score = 0;
  if (password.length >= 8)  score++;
  if (password.length >= 12) score++;      // Longitud extra suma punto
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++; // Carácter especial

  if (score <= 2) return { level: 'weak',   label: 'Débil',  percent: 33  };
  if (score <= 4) return { level: 'medium', label: 'Media',  percent: 66  };
  return            { level: 'strong', label: 'Fuerte', percent: 100 };
}
